import { useMemo, useEffect, useState } from 'react'
import * as THREE from 'three'
import { useThree } from '@react-three/fiber'
import { applyMaterialToObject } from '~/components/ThreeDModel/materials/applyMaterial'
import {
  cloneWithIndependentMaterials,
  createPivotAnchored,
} from '~/components/ThreeDModel/three/cloneObject'
import { disposeObject } from '~/components/ThreeDModel/three/disposeObject'
import {
  beginSceneWork,
  endSceneWork,
} from '~/components/ThreeDModel/three/sceneReadiness'

export interface PanelConfig {
  scale: [number, number, number]
  position: [number, number, number]
  color: string
  anchor?: {
    anchorY?: 'min' | 'center' | 'max'
    anchorZ?: 'min' | 'center' | 'max'
  }
  rotation?: [number, number, number]
}

/**
 * Hook for creating and managing a 3D panel object
 * Handles cloning, scaling, positioning, coloring, and disposal
 * 
 * @param sourceObject - The source THREE.Object3D to clone
 * @param config - Configuration for the panel
 * @returns The configured panel object or null
 * 
 * @example
 * const leftPanel = usePanel(horizontalPanelObject, {
 *   scale: [thickness, height, depth],
 *   position: [-width / 2, 0, 0],
 *   color: selectedColor,
 *   anchor: { anchorY: 'min', anchorZ: 'min' }
 * })
 * 
 * // In render:
 * {leftPanel && <primitive object={leftPanel} />}
 */
export const usePanel = (
  sourceObject: THREE.Object3D | null,
  config: PanelConfig
): THREE.Object3D | null => {
  // Clone in the effect (not useMemo) so the create+dispose lifecycle is
  // paired correctly across StrictMode's mount→unmount→mount cycle.
  // useMemo would cache the disposed clone on the second mount.
  const [panel, setPanel] = useState<THREE.Object3D | null>(null)
  const invalidate = useThree((s) => s.invalidate)

  useEffect(() => {
    if (!sourceObject) {
      return
    }
    const token = beginSceneWork()
    const model = cloneWithIndependentMaterials(sourceObject)
    const built = createPivotAnchored(
      model,
      config.anchor ?? { anchorY: 'min', anchorZ: 'min' }
    )
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPanel(built)
    invalidate()
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => endSceneWork(token))
    )

    return () => {
      cancelAnimationFrame(raf)
      endSceneWork(token)
      disposeObject(built)
      setPanel(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceObject])

  // Scale, position, and rotate the panel
  useEffect(() => {
    if (!panel) return

    panel.scale.set(...config.scale)
    panel.position.set(...config.position)

    if (config.rotation) {
      panel.rotation.set(...config.rotation)
    }

    panel.updateMatrixWorld(true)
    invalidate()
  }, [panel, config.scale, config.position, config.rotation, invalidate])

  // Apply color/texture (with PBR support)
  useEffect(() => {
    if (!panel) return
    applyMaterialToObject(panel, config.color)
    invalidate()
  }, [panel, config.color, invalidate])

  return panel
}

/**
 * Hook for creating multiple panels with the same source object
 * Useful for creating pairs of panels like left/right sides
 * 
 * This implementation properly follows the Rules of Hooks by creating all panels
 * in a single useMemo instead of calling usePanel multiple times in a loop.
 * 
 * @param sourceObject - The source THREE.Object3D to clone
 * @param configs - Array of configurations for each panel
 * @returns Array of configured panel objects
 * 
 * @example
 * const [leftPanel, rightPanel] = usePanels(horizontalPanelObject, [
 *   { scale: [thickness, height, depth], position: [-width / 2, 0, 0], color: selectedColor },
 *   { scale: [thickness, height, depth], position: [width / 2, 0, 0], color: selectedColor }
 * ])
 */
export const usePanels = (
  sourceObject: THREE.Object3D | null,
  configs: PanelConfig[]
): (THREE.Object3D | null)[] => {
  const configCount = configs.length
  const invalidate = useThree((s) => s.invalidate)

  // Anchors only read on (re)clone, so snapshot them to a string keyed
  // off configs identity. Effects don't re-run on shape-equal arrays.
  const anchorsStr = useMemo(
    () => JSON.stringify(configs.map(c => c.anchor ?? { anchorY: 'min', anchorZ: 'min' })),
    [configs]
  )

  // Clone in the effect (not useMemo) so the create+dispose lifecycle is
  // paired correctly across StrictMode's mount→unmount→mount cycle.
  // useMemo would cache the disposed clones on the second mount.
  const [panels, setPanels] = useState<(THREE.Object3D | null)[]>(() =>
    Array(configCount).fill(null)
  )

  useEffect(() => {
    if (!sourceObject) {
      return
    }
    const token = beginSceneWork()
    const anchors = JSON.parse(anchorsStr) as PanelConfig['anchor'][]
    const built = Array.from({ length: configCount }, (_, i) => {
      const model = cloneWithIndependentMaterials(sourceObject)
      return createPivotAnchored(model, anchors[i] ?? { anchorY: 'min', anchorZ: 'min' })
    })
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPanels(built)
    invalidate()
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => endSceneWork(token))
    )

    return () => {
      cancelAnimationFrame(raf)
      endSceneWork(token)
      built.forEach(panel => disposeObject(panel))
      setPanels(Array(configCount).fill(null))
    }
  }, [sourceObject, configCount, anchorsStr, invalidate])

  // Performance optimization: Serialize configs to detect actual value changes
  // This prevents unnecessary effect runs when configs array reference changes
  // but the actual values inside haven't changed
  const configsStr = useMemo(() => JSON.stringify(configs), [configs])

  // Apply scale, position, and rotation to all panels
  useEffect(() => {
    let currentConfigs: PanelConfig[]
    try {
      currentConfigs = JSON.parse(configsStr) as PanelConfig[]
    } catch (error) {
      console.error('Failed to parse panel configs:', error)
      return
    }
    panels.forEach((panel, index) => {
      if (!panel) return

      const config = currentConfigs[index]
      const [scaleX, scaleY, scaleZ] = config.scale
      const [posX, posY, posZ] = config.position

      panel.scale.set(scaleX, scaleY, scaleZ)
      panel.position.set(posX, posY, posZ)

      if (config.rotation) {
        const [rotX, rotY, rotZ] = config.rotation
        panel.rotation.set(rotX, rotY, rotZ)
      }

      panel.updateMatrixWorld(true)
    })
    invalidate()
  }, [panels, configsStr, invalidate])

  // Apply color/texture to all panels (with PBR support)
  useEffect(() => {
    let currentConfigs: PanelConfig[]
    try {
      currentConfigs = JSON.parse(configsStr) as PanelConfig[]
    } catch (error) {
      console.error('Failed to parse panel configs:', error)
      return
    }
    panels.forEach((panel, index) => {
      if (!panel) return
      const config = currentConfigs[index]
      applyMaterialToObject(panel, config.color)
    })
    invalidate()
  }, [panels, configsStr, invalidate])

  return panels
}
